import { ChevronDown } from 'lucide-react'
import type { SelectHTMLAttributes } from 'react'

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly id: string
  /**
   * Width lives on the wrapper, not the select: the chevron is positioned against the
   * wrapper, so sizing the select alone would leave the caret floating away from it.
   */
  readonly wrapperClassName?: string
}

/**
 * A native select with a caret we control.
 *
 * The browser's own caret sits hard against the right edge and is drawn differently by
 * every engine, so a select beside the form's inputs never looks like part of the same
 * system. `appearance-none` plus our own chevron gives consistent breathing room
 * (`pr-10`) and a consistent glyph everywhere.
 *
 * Still a real `<select>`: native keyboard behaviour, native type-ahead, native mobile
 * picker, and it works with no JavaScript. The chevron is `aria-hidden` and
 * `pointer-events-none`, so it is decoration over a working control rather than a
 * replacement for one.
 */
export function Select({ className, wrapperClassName, ...props }: SelectProps) {
  return (
    <div
      className={`relative inline-flex w-full items-center ${wrapperClassName ?? ''}`.trim()}
    >
      <select
        className={[
          'h-11 w-full appearance-none rounded-lg border border-line bg-surface',
          'py-0 pr-10 pl-3 text-base text-ink',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
          'disabled:pointer-events-none disabled:opacity-60',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      />
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 size-4 text-ink-muted"
      />
    </div>
  )
}
